// shared/features/auth/lib/auth-errors.ts
export const AUTH_ERROR_CODES = {
  // Account type conflicts
  CONSUMER_EXISTS: 'CONSUMER_EXISTS',
  PROFESSIONAL_EXISTS: 'PROFESSIONAL_EXISTS',
  USE_PROVIDER: 'USE_PROVIDER',
  EMAIL_AND_PASSWORD_REQUIRED: 'EMAIL_AND_PASSWORD_REQUIRED',

  // OTP/Verification errors
  INVALID_OTP: 'INVALID_OTP',
  EXPIRED_OTP: 'EXPIRED_OTP',
  RATE_LIMITED: 'RATE_LIMITED',
  INVALID_BACKUP_CODE: 'INVALID_BACKUP_CODE',
  INVALID_2FA_CODE: 'INVALID_2FA_CODE',
  MFA_SESSION_EXPIRED: 'MFA_SESSION_EXPIRED',
  MFA_ALREADY_ENABLED: 'MFA_ALREADY_ENABLED',

  // General errors
  INVALID_RESPONSE: 'INVALID_RESPONSE',
  NETWORK_ERROR: 'NETWORK_ERROR',
  UNKNOWN: 'UNKNOWN',
} as const

export type AuthErrorCode = (typeof AUTH_ERROR_CODES)[keyof typeof AUTH_ERROR_CODES]

export class AuthError extends Error {
  code: AuthErrorCode
  data?: Record<string, any>

  constructor(code: AuthErrorCode, message: string, data?: Record<string, any>) {
    super(message)
    this.name = 'AuthError'
    this.code = code
    this.data = data
  }
}

// Map codes to user-friendly messages
export const AUTH_ERROR_MESSAGES: Record<AuthErrorCode, string> = {
  // Account type conflicts
  [AUTH_ERROR_CODES.EMAIL_AND_PASSWORD_REQUIRED]:
    'This account may require email and password. Please sign in with email and password.',
  [AUTH_ERROR_CODES.CONSUMER_EXISTS]:
    'This account is linked to a consumer. Please use a different email or phone number to sign in.',
  [AUTH_ERROR_CODES.PROFESSIONAL_EXISTS]:
    'This account is already registered as a contractor. Please use a different email or phone number to create a homeowner account.',
  [AUTH_ERROR_CODES.USE_PROVIDER]: 'Please sign in with your linked provider instead.',

  // OTP/Verification errors
  [AUTH_ERROR_CODES.INVALID_OTP]: 'The verification code is invalid or expired. Please try again.',
  [AUTH_ERROR_CODES.EXPIRED_OTP]: 'The verification code has expired. Please request a new code.',
  [AUTH_ERROR_CODES.RATE_LIMITED]: 'Too many failed attempts. Please try again in 15 minutes.',
  [AUTH_ERROR_CODES.INVALID_2FA_CODE]: 'The verification code is invalid. Please try again.',
  [AUTH_ERROR_CODES.INVALID_BACKUP_CODE]: 'The recovery code is invalid. Please try again.',
  [AUTH_ERROR_CODES.MFA_SESSION_EXPIRED]: 'The verification session has expired. Please sign in again.',
  [AUTH_ERROR_CODES.MFA_ALREADY_ENABLED]: '2FA is already enabled. Disable it first before setting up a new device.',

  // General errors
  [AUTH_ERROR_CODES.INVALID_RESPONSE]: 'Unable to complete the sign-in process. Please try again.',
  [AUTH_ERROR_CODES.NETWORK_ERROR]: 'Unable to connect. Please check your internet connection.',
  [AUTH_ERROR_CODES.UNKNOWN]: 'An unexpected error occurred. Please try again later.',
}

export const getErrorMessage = (error: unknown): string => {
  if (error instanceof AuthError) {
    // Handle special case for provider errors with dynamic provider name
    if (error.code === AUTH_ERROR_CODES.USE_PROVIDER && error.data?.provider) {
      return `This email is linked to ${error.data.provider}. Please sign in with ${error.data.provider} instead.`
    }
    return AUTH_ERROR_MESSAGES[error.code]
  }

  // Fallback for unexpected errors - never expose raw error messages
  return AUTH_ERROR_MESSAGES[AUTH_ERROR_CODES.UNKNOWN]
}
